import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import Sidebar from '../components/Sidebar';
import './Settings.css';

function Settings() {
  const [adminData, setAdminData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const navigate = useNavigate();
  const { theme } = useTheme();

  const logoUrl = theme === 'light' 
    ? 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7ade8283bc1015dd30.webp'
    : 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7a306550caec6c2c16.webp';

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored || stored === 'undefined') {
      navigate('/');
      return;
    }
    let userData;
    try {
      userData = JSON.parse(stored);
    } catch (e) {
      navigate('/');
      return;
    }
    setAdminData(userData);
    setFullName(userData.full_name || '');
    setEmail(userData.email || '');
    setPhone(userData.phone || 'N/A');
  }, [navigate]);

  // Listen for sidebar collapse changes
  useEffect(() => {
    const handleStorageChange = () => {
      setSidebarCollapsed(localStorage.getItem('sidebarCollapsed') === 'true');
    };
    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(handleStorageChange, 100);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://live-event-photos.onrender.com/api/admin/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to change password');
      }

      setMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAccountInfo = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://live-event-photos.onrender.com/api/admin/update-profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          full_name: fullName,
          email: email,
          phone: phone === 'N/A' ? null : phone
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update profile');
      }

      // Update local storage
      const updatedUser = { ...adminData, full_name: fullName, email: email, phone: phone };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setAdminData(updatedUser);
      setEditMode(false);
      setMessage('Profile updated successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (!adminData) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  const firstName = adminData.full_name?.split(' ')[0] || 'User';
  const isSuperAdmin = adminData.role === 'super_admin';

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <ThemeToggle />
      

      <Sidebar onLogout={handleLogout} isSuperAdmin={isSuperAdmin} />

      <div className="settings-page">
        <h1>Settings</h1>

        <div className="settings-grid">
          {/* Account Info */}
          <div className="settings-card">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
              <h2 style={{margin: 0}}>Account Information</h2>
              {!editMode && (
                <button 
                  onClick={() => setEditMode(true)} 
                  className="btn-primary"
                  style={{padding: '0.5rem 1rem', fontSize: '14px', fontWeight: '500'}}
                >
                  Edit
                </button>
              )}
            </div>
            
            {editMode ? (
              <form onSubmit={handleUpdateAccountInfo} className="password-form">
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="N/A"
                  />
                </div>
                <div style={{display: 'flex', gap: '10px'}}>
                  <button type="submit" className="submit-btn" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setEditMode(false);
                      setFullName(adminData.full_name || '');
                      setEmail(adminData.email || '');
                      setPhone(adminData.phone || 'N/A');
                    }}
                    className="btn-secondary"
                    style={{padding: '1rem'}}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="info-grid">
                <div className="info-row">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{adminData.full_name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{adminData.email}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">{adminData.phone || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Role:</span>
                  <span className="info-value">
                    {adminData.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                  </span>
                </div>
                {adminData.subscription_tier && (
                  <div className="info-row">
                    <span className="info-label">Subscription:</span>
                    <span className="info-value">{adminData.subscription_tier}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Change Password */}
          <div className="settings-card">
            <h2>Change Password</h2>
            <form onSubmit={handleChangePassword} className="password-form">
              {message && <div className="success-message">{message}</div>}
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                  required
                  disabled={loading}
                  minLength={8}
                />
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  disabled={loading}
                  minLength={8}
                />
              </div>

              <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem'}}>
                <button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={loading}
                  style={{padding: '0.5rem 1rem', fontSize: '14px', fontWeight: '500'}}
                >
                  {loading ? 'Changing Password...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      
    </div>
  );
}

export default Settings;
